/**
 * The Google calendar connection as the Kalendarz tab and the return page decide it
 * (calendar-sync-requirements R2, R8, R10). Pure.
 */

import type { TrainWithMeModulesTrainingDtosResponsesGoogleCalendarConnectionResponse as GoogleConnectionDto } from '~/shared/api/generated/model';
import { parseInstant } from '~/shared/lib/calendarDate';

export type GoogleCalendarView =
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'notConnected' }
  | {
      /** `broken`: Google stopped accepting the grant; the account and the last write still stand (R10). */
      readonly kind: 'connected' | 'broken';
      readonly email: string | null;
      /** Epoch ms, or `null` before the first pass has written anything. */
      readonly lastSyncedAt: number | null;
    };

export function googleCalendarView(dto: GoogleConnectionDto): GoogleCalendarView {
  if (!dto.available) return { kind: 'unavailable' };
  if (!dto.connected) return { kind: 'notConnected' };
  const email =
    typeof dto.accountEmail === 'string' && dto.accountEmail !== '' ? dto.accountEmail : null;
  const lastSyncedAt = parseInstant(dto.lastSyncedAt);
  return { kind: dto.status === 'Broken' ? 'broken' : 'connected', email, lastSyncedAt };
}

/* ── The first fill ─────────────────────────────────────────────────────────────────────────── */

const FIRST_FILL_POLL_MS = 4000;
const FIRST_FILL_WINDOW_MS = 2 * 60 * 1000;

export interface FirstFillPoll {
  /** When the fill was first seen pending; `null` once it is not. */
  readonly pendingSince: number | null;
  /** The next re-read, or `false` for none. */
  readonly interval: number | false;
}

/**
 * The POST only queues the first fill, so nothing else would turn „Dodajemy…" into the last-sync
 * line (R8). Bounded, so a fill that keeps failing does not poll for ever.
 */
export function firstFillPoll(
  dto: GoogleConnectionDto | undefined,
  pendingSince: number | null,
  now: number,
): FirstFillPoll {
  const view = dto === undefined ? null : googleCalendarView(dto);
  if (view?.kind !== 'connected' || view.lastSyncedAt !== null) return { pendingSince: null, interval: false };
  const since = pendingSince ?? now;
  return { pendingSince: since, interval: now - since < FIRST_FILL_WINDOW_MS ? FIRST_FILL_POLL_MS : false };
}

/** A `refetchInterval` for one screen: each re-read moves `dataUpdatedAt`, so the clock is kept here. */
export function firstFillRefetchInterval(now: () => number = Date.now) {
  let pendingSince: number | null = null;
  return (dto: GoogleConnectionDto | undefined): number | false => {
    const poll = firstFillPoll(dto, pendingSince, now());
    pendingSince = poll.pendingSince;
    return poll.interval;
  };
}

export type DisconnectConfirmBody =
  | 'disconnectConfirmBody'
  | 'disconnectConfirmBodyNoEmail'
  | 'disconnectConfirmBodyBroken'
  | 'disconnectConfirmBodyBrokenNoEmail';

/** What the disconnect confirm promises: a broken grant can no longer remove the calendar (R8, R9). */
export function disconnectConfirmBody(view: GoogleCalendarView | null): DisconnectConfirmBody {
  const broken = view?.kind === 'broken';
  const email = view?.kind === 'connected' || view?.kind === 'broken' ? view.email : null;
  if (broken) return email === null ? 'disconnectConfirmBodyBrokenNoEmail' : 'disconnectConfirmBodyBroken';
  return email === null ? 'disconnectConfirmBodyNoEmail' : 'disconnectConfirmBody';
}

/**
 * Whether the strip offers Kalendarz. An answer decides it; with none yet (or a failed read) only
 * an address naming the tab keeps it, so the wait or the error has somewhere to stand.
 */
export function calendarTabShown(view: GoogleCalendarView | null, selected: boolean): boolean {
  return view === null ? selected : view.kind !== 'unavailable';
}

/* ── The return from Google ─────────────────────────────────────────────────────────────────── */

/** What the tab is told after the return page, beside `?tab=integracje`. A cancel says nothing. */
export const GOOGLE_CONNECT_OUTCOMES = ['connected', 'scopeMissing', 'failed'] as const;

export type GoogleConnectOutcome = (typeof GOOGLE_CONNECT_OUTCOMES)[number];

export function isGoogleConnectOutcome(value: unknown): value is GoogleConnectOutcome {
  return (
    typeof value === 'string' && (GOOGLE_CONNECT_OUTCOMES as readonly string[]).includes(value)
  );
}

/** The contract's own ceilings on the POST body. */
const CODE_MAX = 2048;
const STATE_MAX = 256;

export type GoogleReturnStep =
  | { readonly kind: 'post'; readonly code: string; readonly state: string }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'failed' };

export interface GoogleReturnInput {
  readonly code: string | null;
  readonly state: string | null;
  readonly error: string | null;
  /** The state this tab stashed before leaving, or `null` when there is none. */
  readonly stashed: string | null;
}

/** Whether the return may be sent. A state this tab did not stash is never sent. */
export function googleReturnStep({ code, state, error, stashed }: GoogleReturnInput): GoogleReturnStep {
  if (error !== null) return error === 'access_denied' ? { kind: 'cancelled' } : { kind: 'failed' };
  const noCode = code === null || code === '';
  const noState = state === null || state === '';
  // Nothing on the address — a reload after the scrub, or a bare visit.
  if (noCode && noState) return { kind: 'cancelled' };
  if (noCode || code.length > CODE_MAX) return { kind: 'failed' };
  // Nothing stashed (storage blocked or cleared): nothing to match, so nothing is sent and the read decides.
  if (stashed === null) return { kind: 'cancelled' };
  if (noState || state.length > STATE_MAX || state !== stashed) return { kind: 'failed' };
  return { kind: 'post', code, state };
}

/**
 * A refused POST, judged after the read was asked again: a code works once, so a retry that
 * reached the backend first may already have connected.
 */
export function connectFailureOutcome(
  problemCode: string | undefined,
  reread: GoogleCalendarView | null,
): GoogleConnectOutcome {
  if (reread?.kind === 'connected') return 'connected';
  return problemCode === 'calendar_scope_not_granted' ? 'scopeMissing' : 'failed';
}

/** A POST that answered: connected is the only success. */
export function connectAnswerOutcome(view: GoogleCalendarView): GoogleConnectOutcome {
  return view.kind === 'connected' ? 'connected' : 'failed';
}
